<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

const TOOLTIP_MIN_WIDTH = 280;
const EDGE_OFFSET = 80;

defineProps<{
	label?: string;
	positionBottom?: boolean;
}>();

const isHovered = ref(false);
const isClicked = ref(false);
const remainingSpace = ref({ left: 0, right: 0 });

const tooltip =
	ref(); /** The HTML element ref, used to get position on screen and calculate left and right */

const computedPositionLeft = computed(() => {
	if (remainingSpace.value.left <= 0) return '0px';

	if (remainingSpace.value.left <= TOOLTIP_MIN_WIDTH / 2)
		return `-${remainingSpace.value.left}px`;

	return '';
});

const computedPositionRight = computed(() => {
	if (remainingSpace.value.right <= 0) return '0px';

	if (remainingSpace.value.right <= TOOLTIP_MIN_WIDTH / 2)
		return `-${remainingSpace.value.right}px`;

	if (tooltip.value?.getBoundingClientRect().left <= TOOLTIP_MIN_WIDTH + EDGE_OFFSET)
		return `-${TOOLTIP_MIN_WIDTH}`;

	return `calc(50% - ${TOOLTIP_MIN_WIDTH / 2}px`;
});

const getRemainingSpace = () => {
	const windowWidth = window?.innerWidth || 0;

	remainingSpace.value.left = tooltip.value?.getBoundingClientRect().left - EDGE_OFFSET;
	remainingSpace.value.right =
		windowWidth - tooltip.value?.getBoundingClientRect().right - EDGE_OFFSET;
};

const onClickOutside = () => {
	if (isClicked.value) isClicked.value = false;
};

onMounted(async () => {
	window.addEventListener('resize', getRemainingSpace);
	await nextTick();
	getRemainingSpace();
});

onBeforeUnmount(() => {
	window.removeEventListener('resize', getRemainingSpace);
});
</script>
<template>
	<div
		v-click-outside="onClickOutside"
		class="info-tooltip"
		@mouseover="isHovered = true"
		@mouseout="isHovered = false"
	>
		<button
			:class="['bx', isHovered || isClicked ? 'bxs-info-circle' : ' bx-info-circle']"
			ref="tooltip"
			class="info-tooltip__button"
			type="button"
			tabindex="0"
			:aria-label="label"
			@click.stop="isClicked = !isClicked"
		/>
		<div
			v-if="isHovered || isClicked"
			role="tooltip"
			:aria-hidden="isHovered || isClicked"
			:style="{ left: computedPositionLeft, right: computedPositionRight }"
			:class="[
				'info-tooltip__label',
				{
					'info-tooltip__label--bottom': positionBottom,
				},
			]"
		>
			<slot>
				{{ label }}
			</slot>
		</div>
	</div>
</template>
<style lang="scss" scoped>
$tooltip-width: 280px;
$tooltip-icon-size: 20px;

.info-tooltip {
	display: inline-flex;
	position: relative;
	width: $tooltip-icon-size;
	height: $tooltip-icon-size !important;
	white-space: pre-wrap;

	&__button {
		height: 100%;
		width: 100%;
		font-size: 16px;
		color: $color-gray-900;

		&:focus-visible {
			outline: $border-width-2 $border-style $color-focus;
			border-radius: 50%;
		}
	}

	&__label {
		font-size: $font-size-sm;
		font-weight: $font-weight-medium;
		line-height: $line-height-sm;
		border-radius: $border-radius-2;
		padding: $spacing-3;
		position: absolute;
		text-align: center;
		bottom: $tooltip-icon-size;
		width: $tooltip-width;
		z-index: $z-index-1;
		background-color: $color-gray-900;
		color: $color-gray-50;

		&::selection {
			color: $color-gray-950;
			background: $color-primary;
		}

		&--bottom {
			top: 100%;
			bottom: auto;
		}
	}
}
</style>
